import React from "react";

export default function StatusGate({
  endpoint,
  ok,
  brokenUI,
  loadingUI,
  refreshMs,
  fetchInit,
  errorPolicy = "failClosed",
}) {
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const loadingStartRef = React.useRef(Date.now());
  const MIN_LOADING_MS = 1500;

  const load = React.useCallback(async () => {
    try {
      setLoading(true);
      loadingStartRef.current = Date.now();
      setError(null);

      const ctrl = new AbortController();
      const timeout = setTimeout(() => ctrl.abort(), 8000);

      const res = await fetch(endpoint, {
        cache: "no-store",
        ...(fetchInit || {}),
        signal: ctrl.signal,
      });
      clearTimeout(timeout);

      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setData({ ...json, isBroken: Boolean(json?.isBroken) });
    } catch (e) {
      setError(e?.message || "Failed to fetch status");
    } finally {
      const elapsed = Date.now() - loadingStartRef.current;
      const remaining = MIN_LOADING_MS - elapsed;
      if (remaining > 0) {
        setTimeout(() => setLoading(false), remaining);
      } else {
        setLoading(false);
      }
    }
  }, [endpoint, fetchInit]);

  React.useEffect(() => {
    load();
  }, [load]);

  React.useEffect(() => {
    if (!refreshMs) return;
    const t = setInterval(load, refreshMs);
    return () => clearInterval(t);
  }, [refreshMs, load]);

  if (loading) {
    return (
      loadingUI ?? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100vh",
            width: "100vw",
            background: "#0C1F3F",
          }}
        >
          <img
            src="/favicon.ico"
            alt="Loading..."
            style={{
              width: 64,
              height: 57,
              animation: "pulse 1.5s infinite",
            }}
          />
          <style>
            {`
              @keyframes pulse {
                0% { transform: scale(1); opacity: 1; }
                50% { transform: scale(1.2); opacity: 0.6; }
                100% { transform: scale(1); opacity: 1; }
              }
            `}
          </style>
        </div>
      )
    );
  }

  if (error) {
    if (errorPolicy === "failOpen") return <>{ok}</>;
    return (
      <div
        style={{
          padding: 16,
          borderRadius: 14,
          border: "1px solid #fecaca",
          background: "#fef2f2",
        }}
      >
        <div style={{ fontWeight: 600, color: "#991b1b" }}>
          Status check failed
        </div>
        <div style={{ color: "#7f1d1d", fontSize: 14, marginTop: 6 }}>
          {error}
        </div>
        <button
          onClick={load}
          style={{
            marginTop: 10,
            padding: "6px 10px",
            borderRadius: 10,
            border: "1px solid #e5e7eb",
            background: "#fff",
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  if (!data) return null;

  if (data.isBroken) {
    if (brokenUI) return <>{brokenUI(data)}</>;
    return (
      <div
        style={{
          padding: 20,
          borderRadius: 14,
          border: "1px solid #fde68a",
          background: "#fffbeb",
        }}
      >
        <h2 style={{ margin: 0, fontSize: 18 }}>
          {data.name || "Project"} is currently unavailable
        </h2>
        <p style={{ marginTop: 6, opacity: 0.8 }}>
          {data.message || "Please contact Admin"}
        </p>
      </div>
    );
  }

  return <>{ok}</>;
}
